// src/steps/EmergencyContactsStep.tsx
import React from "react";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import type { EmergencyContactState } from "../wizard/wizardState";
import { useListManagement } from "../hooks/useListManagement";
import { formatPhoneNumber } from "../utils/phoneFormatter";

type Props = {
  value: EmergencyContactState[];
  onChange: (next: EmergencyContactState[]) => void;
  errors: Record<string, string>;
};

function createBlankContact(): EmergencyContactState {
  return { firstName: "", lastName: "", phone: "", relation: "" };
}

export default function EmergencyContactsStep({ value, onChange, errors }: Props) {
  const { updateItem, addItem, removeItem } = useListManagement(value, {
    createBlank: createBlankContact
  });

  const updateContact = (index: number, next: EmergencyContactState) =>
    onChange(updateItem(index, next));
  const addContact = () => onChange(addItem());
  const removeContact = (index: number) => onChange(removeItem(index));

  return (
    <div className="space-y-4">
      {errors.emergencyContacts ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {errors.emergencyContacts}
        </div>
      ) : null}

      {value.map((c, idx) => {
        const firstKey = `em_${idx}_firstName`;
        const lastKey = `em_${idx}_lastName`;
        const phoneKey = `em_${idx}_phone`;
        const relKey = `em_${idx}_relation`;

        return (
          <div key={idx} className="rounded-xl border border-slate-200 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold">Emergency Contact {idx + 1}</h3>
              <Button
                type="button"
                variant="ghost"
                onClick={() => removeContact(idx)}
                className="px-3 py-2"
              >
                Remove
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="First Name"
                value={c.firstName}
                onChange={(e) => updateContact(idx, { ...c, firstName: e.target.value })}
                error={errors[firstKey]}
              />
              <Input
                label="Last Name"
                value={c.lastName}
                onChange={(e) => updateContact(idx, { ...c, lastName: e.target.value })}
                error={errors[lastKey]}
              />
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Phone"
                value={c.phone}
                onChange={(e) => updateContact(idx, { ...c, phone: formatPhoneNumber(e.target.value) })}
                error={errors[phoneKey]}
                inputMode="tel"
                placeholder="(xxx) xxx-xxxx"
              />
              <Input
                label="Relation"
                value={c.relation}
                onChange={(e) => updateContact(idx, { ...c, relation: e.target.value })}
                error={errors[relKey]}
                placeholder="Aunt, Uncle, Neighbor, etc."
              />
            </div>
          </div>
        );
      })}

      <Button type="button" variant="secondary" className="w-full" onClick={addContact}>
        Add Another Emergency Contact
      </Button>
    </div>
  );
}
